import { FormEvent, useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "Redux/hooks";
import {
  createSupplierCustomer,
  updateSupplierCustomer,
} from "Services/supplier-customers";

import "./index.scss";

const CUSTOMER_STATUSES = ["ACTIVE", "NEW", "INACTIVE"] as const;

const EMPTY_FORM = {
  name: "",
  business_number: "",
  contact_name: "",
  phone: "",
  email: "",
  address: "",
  status: "ACTIVE",
};

type CustomerForm = typeof EMPTY_FORM;

type SupplierCustomerModalProps = {
  show: boolean;
  customer?: any;
  initialValues?: Partial<CustomerForm>;
  onClose: () => void;
  onCreated?: (customer: any) => void;
  onSaved?: (customer: any) => void;
};

function SupplierCustomerModal({
  show,
  customer = null,
  initialValues,
  onClose,
  onCreated,
  onSaved,
}: SupplierCustomerModalProps) {
  const { t } = useTranslation(["myOrder"]);
  const rightToLeft = useAppSelector((state) => state.root.rightToLeft);

  const [form, setForm] = useState<CustomerForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!show) return;

    const primaryContact =
      customer?.contacts?.find((contact: any) => contact.is_primary) ||
      customer?.contacts?.[0];

    setForm(
      customer
        ? {
            name: customer.name || "",
            business_number: customer.business_number || "",
            contact_name: primaryContact?.name || "",
            phone: customer.phone || primaryContact?.phone || "",
            email: customer.email || primaryContact?.email || "",
            address: customer.address || "",
            status: customer.status || "ACTIVE",
          }
        : {
            ...EMPTY_FORM,
            ...initialValues,
            status: initialValues?.status || "ACTIVE",
          },
    );

    setError("");
  }, [show, customer, initialValues]);

  if (!show) return null;

  const updateField = (field: keyof CustomerForm, value: string) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!form.name.trim() || !form.business_number.trim()) return;

    try {
      setSaving(true);
      setError("");

      const payload = {
        ...form,
        name: form.name.trim(),
        business_number: form.business_number.trim(),
      };

      const response = customer
        ? await updateSupplierCustomer(customer.id, payload)
        : await createSupplierCustomer(payload);

      const savedCustomer = response?.data || response;

      onSaved?.(savedCustomer);
      onCreated?.(savedCustomer);
    } catch (submitError: any) {
      console.error("Failed to save supplier customer", submitError);

      const responseData = submitError?.response?.data;

      if (responseData?.business_number) {
        setError(
          Array.isArray(responseData.business_number)
            ? responseData.business_number[0]
            : responseData.business_number,
        );
      } else {
        setError(t("supplier customer save failed"));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="supplier-modal-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <form
        className="supplier-customer-modal"
        dir={rightToLeft ? "rtl" : "ltr"}
        onSubmit={submit}
      >
        <button
          type="button"
          className="supplier-modal-close"
          aria-label={t("close window")}
          onClick={onClose}
        >
          ×
        </button>

        <h3>
          {customer
            ? t("edit customer modal title")
            : t("add customer modal title")}
        </h3>

        <p>
          {customer
            ? t("edit customer modal subtitle")
            : t("add customer modal subtitle")}
        </p>

        <div className="supplier-customer-form-grid">
          <label>
            <span>{t("supplier customer name")} *</span>
            <input
              required
              value={form.name}
              onChange={(event) => updateField("name", event.target.value)}
              placeholder={t("supplier customer name placeholder")}
            />
          </label>

          <label>
            <span>{t("supplier business number")} *</span>
            <input
              required
              value={form.business_number}
              onChange={(event) =>
                updateField("business_number", event.target.value)
              }
              placeholder={t("supplier business number placeholder")}
            />
          </label>

          <label>
            <span>{t("supplier contact person")}</span>
            <input
              value={form.contact_name}
              onChange={(event) =>
                updateField("contact_name", event.target.value)
              }
              placeholder={t("supplier contact placeholder")}
            />
          </label>

          <label>
            <span>{t("supplier phone")}</span>
            <input
              value={form.phone}
              onChange={(event) => updateField("phone", event.target.value)}
              placeholder="05X-XXXXXXX"
            />
          </label>

          <label>
            <span>{t("supplier email")}</span>
            <input
              type="email"
              value={form.email}
              onChange={(event) => updateField("email", event.target.value)}
              placeholder="name@company.com"
            />
          </label>

          <label>
            <span>{t("supplier address")}</span>
            <input
              value={form.address}
              onChange={(event) => updateField("address", event.target.value)}
              placeholder={t("supplier address placeholder")}
            />
          </label>
        </div>

        <label className="supplier-modal-status">
          <span>{t("supplier customer status")}</span>
          <select
            value={form.status}
            onChange={(event) => updateField("status", event.target.value)}
          >
            {CUSTOMER_STATUSES.map((status) => (
              <option value={status} key={status}>
                {t(`customer status ${status}`)}
              </option>
            ))}
          </select>
        </label>

        {error && <p className="supplier-modal-error">{error}</p>}

        <div className="supplier-modal-actions">
          <button type="button" onClick={onClose}>
            {t("supplier cancel")}
          </button>

          <button
            type="submit"
            className="supplier-modal-submit"
            disabled={
              saving || !form.name.trim() || !form.business_number.trim()
            }
          >
            {saving ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <>
                {!customer && <span aria-hidden="true">＋</span>}
                {customer
                  ? t("supplier update customer submit")
                  : t("supplier add customer submit")}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

export default SupplierCustomerModal;
